import React from 'react';
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';

export type NoteSeverity = 'success' | 'info' | 'warning' | 'error';

type NotePanelProps = {
  severity: NoteSeverity;
  title?: React.ReactNode;
  body?: React.ReactNode;
  action?: React.ReactNode;
  // Extra content under the text, such as links.
  children?: React.ReactNode;
};

// The drawer's one notice style: a tinted outlined panel with a status dot, rounded like its summary boxes.
export function NotePanel({ severity, title, body, action, children }: NotePanelProps) {
  return (
    <Paper
      variant="outlined"
      role={severity === 'error' ? 'alert' : 'status'}
      sx={(theme) => {
        const tone = theme.palette[severity];
        return {
          px: 2,
          py: 1.25,
          borderRadius: 2,
          bgcolor: alpha(tone.main, 0.04),
          borderColor: alpha(tone.main, 0.2)
        };
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        {/* Padding, not margin: Stack spacing resets its children's margins. Lines the dot up with the first line. */}
        <Box sx={{ pt: '5px', flexShrink: 0 }}>
          <Box sx={(theme) => ({ width: 7, height: 7, borderRadius: '50%', bgcolor: theme.palette[severity].main })} />
        </Box>
        <Stack spacing={0.15} sx={{ minWidth: 0, flex: 1 }}>
          {title && (
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3, fontSize: 13 }}>
              {title}
            </Typography>
          )}
          {/* Without a title the body is the message, so it reads at full strength. */}
          {body && (
            <Typography
              variant="caption"
              color={title ? 'text.secondary' : 'text.primary'}
              sx={{ fontSize: title ? 11.5 : 12.5, lineHeight: 1.4 }}
            >
              {body}
            </Typography>
          )}
          {children}
        </Stack>
        {action && <Box sx={{ alignSelf: 'center', flexShrink: 0 }}>{action}</Box>}
      </Stack>
    </Paper>
  );
}
